import React, { ReactNode } from 'react';
import '../styles/BillsForm.scss';
import { Heading, Button, Icon } from '@innovationnorway/cl-react';
import { Table } from '@innovationnorway/component-library';
import { useFieldArray, useForm } from 'react-hook-form';
import InputWrapper from 'src/components/Form/InputWrapper';
import { requiredI18n } from 'src/utils/validation';
import { useTranslation } from 'react-i18next';
import { SelectWrapper } from 'src/components/Form/SelectWrapper';
import { calculateInnovationSupport, calculateSupportIntensity } from '../utils/calculations';
import BillSupportNumberHandler from './BillSupportNumberHandler';
import { SivaEsaReportingDto, SivaPhase } from 'src/api/v2';
import useCompanyContract from '../hooks/use-company-contract';

type Props = {
  header: string;
  bills?: SivaEsaReportingDto[];
  numericId?: number;
  onSubmit: (bills: SivaEsaReportingDto[]) => void;
  onCancel: () => void;
  selectedYear: number;
  isLoading?: boolean;
  hideRemoveOption?: boolean;
};

type BillsFormData = {
  bills: SivaEsaReportingDto[];
};

const BillsForm: React.FC<Props> = ({
  header,
  bills,
  numericId,
  onSubmit,
  onCancel,
  selectedYear,
  isLoading = false,
  hideRemoveOption = false,
}) => {
  const { t: tCommon } = useTranslation();

  const { t: tError } = useTranslation('error');
  const { t: tTableTitles } = useTranslation('tableTitles');
  const { t: tSiva } = useTranslation('siva');
  const { contract, currentPhase, company } = useCompanyContract(numericId);

  const emptyBill: SivaEsaReportingDto = {
    sivaContractNumericalId: numericId ?? 0,
    date: new Date(),
    id: undefined,
    invoiceNumber: '',
    sivaPhase: company?.contracts[0].phases[0].sivaPhase || SivaPhase.PreIncubation,
    marketValue: 0,
    invoicedDeductible: 0,
    sivaContractName: undefined,
  };

  const phaseOptions = React.useMemo(() => {
    if (company) {
      const phases = [
        ...new Set(
          company.contracts.flatMap((contract) =>
            contract.phases
              .filter((phase) =>
                phase.toDate ? new Date(phase.toDate).getFullYear() >= selectedYear : phase.toDate === null,
              )
              .filter((phase) => phase.sivaPhase !== SivaPhase.Idea)
              .filter((phase) => phase.sivaPhase !== SivaPhase.Alumni)
              .map((phase) => phase.sivaPhase),
          ),
        ),
      ];
      return phases.map((phase) => ({
        value: phase,
        text: tSiva(`phase.${phase}` as any, { defaultValue: phase }),
      }));
    } else if (
      contract &&
      (currentPhase?.sivaPhase === SivaPhase.PreIncubation || currentPhase?.sivaPhase === SivaPhase.Alumni)
    ) {
      // This is an edge-case to handle contracts in pre-incubation without a company (yet).
      // We were told we would not have to handle this, but ... lies!
      const phases = [
        ...new Set(
          contract.phases
            .filter((phase) =>
              phase.toDate ? new Date(phase.toDate).getFullYear() >= selectedYear : phase.toDate === null,
            )
            .filter((phase) => phase.sivaPhase !== SivaPhase.Idea)
            .filter((phase) => phase.sivaPhase !== SivaPhase.Alumni)
            .map((phase) => phase.sivaPhase),
        ),
      ];
      return phases.map((phase) => ({
        value: phase,
        text: tSiva(`phase.${phase}` as any, { defaultValue: phase }),
      }));
    }
    return [];
  }, [company, contract, currentPhase?.sivaPhase, selectedYear, tSiva]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<BillsFormData>({
    mode: 'onBlur',
    defaultValues: {
      bills: bills || [
        {
          ...emptyBill,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bills',
  });
  const watchFields = watch('bills');
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFields[index],
    };
  });

  const addEmptyBill = () => {
    append({
      ...emptyBill,
    });
  };

  const convertBillToTableRow = (index: number, bill: SivaEsaReportingDto) => {
    const removeButton = (
      <Button
        type="button"
        icon
        variant="tertiary"
        aria-label={tCommon('remove')}
        onClick={() => remove(index)}
      >
        <Icon prefix="fasr" iconName="times" />
      </Button>
    );

    const billNr = (
      <InputWrapper
        {...register(`bills.${index}.invoiceNumber`)}
        error={errors.bills?.[index]?.invoiceNumber?.message}
        className={'margin--0'}
      />
    );

    const phase = (
      <SelectWrapper
        options={phaseOptions}
        {...register(`bills.${index}.sivaPhase`, {
          required: requiredI18n(tError),
        })}
        error={errors.bills?.[index]?.sivaPhase?.message}
        className={'margin--0'}
      />
    );

    const marketValue = (
      <InputWrapper
        type="number"
        {...register(`bills.${index}.marketValue`, {
          required: requiredI18n(tError),
          valueAsNumber: true,
        })}
        error={errors.bills?.[index]?.marketValue?.message}
        className={'margin--0'}
      />
    );

    const deductible = (
      <InputWrapper
        type="number"
        {...register(`bills.${index}.invoicedDeductible`, {
          required: requiredI18n(tError),
          valueAsNumber: true,
        })}
        error={errors.bills?.[index]?.invoicedDeductible?.message}
        className={'margin--0'}
      />
    );

    const innovationSupportCalculation = calculateInnovationSupport(
      bill.marketValue,
      bill.invoicedDeductible,
    );

    const innovationSupport = (
      <div className="text-align--right">
        <BillSupportNumberHandler
          value={innovationSupportCalculation}
          hide={!bill.marketValue && !bill.invoicedDeductible}
        />
      </div>
    );

    const supportIntensityCalculation = calculateSupportIntensity(bill.marketValue, bill.invoicedDeductible);

    const supportIntensity = (
      <div>
        <BillSupportNumberHandler
          formattedValue={`${supportIntensityCalculation} %`}
          hide={!bill.marketValue && !bill.invoicedDeductible}
          value={supportIntensityCalculation}
        />
      </div>
    );

    let cells = [
      removeButton,
      billNr,
      phase,
      marketValue,
      deductible,
      innovationSupport,
      supportIntensity,
    ] as ReactNode[];

    if (hideRemoveOption) {
      cells = cells.slice(1);
    }

    return cells;
  };

  const onSubmitHandler = handleSubmit((formData: BillsFormData) => {
    onSubmit(formData.bills);
  });

  const tableHeaders = [
    <span key="empty" aria-label={tCommon('remove')}></span>,
    tTableTitles('billNumber'),
    tCommon('phase'),
    tTableTitles('marketValue'),
    tTableTitles('deductible'),
    tTableTitles('innovation-support'),
    tTableTitles('support-intensity'),
  ];

  return (
    <form className="siva-esa-bills-form__form" onSubmit={onSubmitHandler}>
      <div className="siva-esa-bills-form__header">
        <Heading level={3}>{header}</Heading>
      </div>
      <div className="margin-left--1 margin-right--1 margin-top--6 margin-bottom--6">
        <Table
          thead={hideRemoveOption ? tableHeaders.slice(1) : tableHeaders}
          rows={controlledFields.map((field, index) => ({
            cells: convertBillToTableRow(index, field),
          }))}
          caption=""
          id="siva-esa-bills-form-table"
        />
        <Button type="button" variant="tertiary" className="margin-top--1" onClick={() => addEmptyBill()}>
          <Icon prefix="fasr" iconName="plus" />
          {tCommon('addNewRow')}
        </Button>
      </div>
      <div className="margin-top--6 display--flex gap--1 margin-left--3">
        <Button type="submit" loading={isLoading}>
          {tCommon('save')}
        </Button>
        <Button type="button" variant="secondary" loading={isLoading} onClick={onCancel}>
          {tCommon('cancel')}
        </Button>
      </div>
    </form>
  );
};

export default BillsForm;
